import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import style from "./framework.module.css";

type Metric = { value: string; label: string };
type Card = { title: string; description: string; tags?: string[] };
type Approach = { title: string; description: string };
type Capability = { title: string; description: string };
type Step = { num: string; title: string; description: string };
type Quality = { title: string; description: string };

const METRICS: Metric[] = [
  { value: "100+", label: "Projects" },
  { value: "99%", label: "Success Rate" },
  { value: "3", label: "Major Clients" },
  { value: "24/7", label: "Support" },
];

const SOLUTION_AREAS: Card[] = [
  {
    title: "Display Manufacturing Systems",
    description: "디스플레이 제조 공정의 복잡한 요구사항을 충족하는 정밀 자동화 시스템",
    tags: ["Assembly Line", "Quality Control", "Process Monitoring"],
  },
  {
    title: "Semiconductor Equipment Integration",
    description: "반도체 생산라인의 기존 시스템과 seamless하게 연동되는 고정밀 자동화 장비",
    tags: ["PLC Integration", "SCADA", "Real-time Control"],
  },
  {
    title: "Battery Production Automation",
    description: "2차 전지 제조 공정의 안전성과 효율성을 보장하는 자동화 시스템",
    tags: ["Safety Systems", "Environmental Control", "Data Analytics"],
  },
];

const APPROACH: Approach[] = [
  {
    title: "Site-Centered Engineering",
    description:
      "실제 제조 현장의 조건과 제약사항을 정확히 파악하여 현실적이고 구현 가능한 솔루션을 설계합니다.",
  },
  {
    title: "Incremental Implementation",
    description:
      "기존 운영에 미치는 영향을 최소화하면서 단계적으로 시스템을 구축하고 안정화합니다.",
  },
  {
    title: "Proven Technology Stack",
    description:
      "검증된 기술과 산업 표준을 기반으로 안정성과 호환성을 보장하는 시스템을 구축합니다.",
  },
  {
    title: "Continuous Partnership",
    description:
      "구축 후에도 지속적인 기술 지원과 시스템 개선을 통해 장기적인 파트너십을 유지합니다.",
  },
];

const CAPABILITIES: Capability[] = [
  { title: "Natural Language Processing", description: "자연어 입력을 통한 직관적인 설계 요구사항 분석" },
  { title: "Automated 2D/3D Generation", description: "생성형 AI를 활용한 도면 및 3D 모델 자동 생성" },
  { title: "Design Validation", description: "AI 기반 설계 검증 및 최적화 솔루션" },
  { title: "Legacy Integration", description: "기존 CAD 시스템과의 seamless한 연동" },
];

const STEPS: Step[] = [
  { num: "01", title: "Site Survey", description: "현장 조사 및 기존 시스템 분석을 통한 정확한 요구사항 파악" },
  { num: "02", title: "System Design", description: "현장 조건에 최적화된 맞춤형 시스템 설계" },
  { num: "03", title: "Prototyping", description: "실제 환경에서의 프로토타입 테스트 및 기능 검증" },
  { num: "04", title: "Integration", description: "기존 시스템과의 안전한 통합 구현" },
  { num: "05", title: "Stabilization", description: "운영 환경 배포 및 장기 안정성 확보" },
];

const QUALITY: Quality[] = [
  {
    title: "Field Testing Standards",
    description:
      "실제 제조 환경에서의 철저한 테스트를 통해 시스템의 안정성과 신뢰성을 검증합니다.",
  },
  {
    title: "Complete Documentation",
    description:
      "운영 매뉴얼부터 기술 문서까지 체계적인 문서화로 효율적인 운영을 지원합니다.",
  },
  {
    title: "Operator Training",
    description:
      "현장 운영자를 위한 맞춤형 교육 프로그램으로 시스템의 안전하고 효율적인 운영을 보장합니다.",
  },
  {
    title: "Preventive Maintenance",
    description:
      "예방 정비 체계와 실시간 모니터링을 통해 시스템의 최적 성능을 지속적으로 유지합니다.",
  },
];

function MetricBlock({ m }: { m: Metric }) {
  return (
    <div style={{ textAlign: "center", padding: 20, background: "rgba(52,152,219,.1)", borderRadius: 4 }}>
      <span style={{ display: "block", fontSize: 32, color: "#3498db", fontWeight: 600 }}>{m.value}</span>
      <span style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>{m.label}</span>
    </div>
  );
}

export default function Framework() {
  return (
    <>
      <Header />
        <div className={style["main-content"]}>
            {/* HERO */}
            <section className={style["container--front"]} style={{ background: "linear-gradient(135deg,#2c3e50,#3498db)" }}>
                <div className={style["unit--front--head"]}>
                <h1 className={style["text--front--head"]}>Framework</h1>
                <p className={style["text--front--copy"]} style={{ paddingTop: 12, fontSize: 18 }}>
                    Field-Proven Engineering Excellence
                    <br />
                    현장 중심의 체계적 접근법으로 신뢰할 수 있는 자동화 솔루션을 구축합니다
                </p>
                </div>
            </section>

            {/* Core Solution Stack */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <div className={style["text--content"]} style={{ marginBottom: 8 }}>
                    TOTAL Solution Stack
                </div>
                <p className={style["text--content--copy"]} style={{ width: "min(700px,100%)" }}>
                    검증된 기술과 현장 경험으로 완성된 통합 자동화 솔루션
                </p>

                <div className={style.stackGrid}>
                    {/* 좌측 고정 개요 카드 */}
                    <aside className={style.stickyBox} style={{
                        background: "linear-gradient(145deg,#2a2a2a,#1e1e1e)",
                        border: "1px solid #333",
                        borderLeft: "4px solid #3498db",
                        borderRadius: 8,
                        padding: 32,
                    }}>
                    <div style={{ fontSize: 18, fontWeight: 300, marginBottom: 16, color: "#fff", textTransform: "uppercase", letterSpacing: 1 }}>
                        Proven Track Record
                    </div>
                    <p style={{ color: "#ccc", lineHeight: 1.8, marginBottom: 22 }}>
                        삼성, LG, SK하이닉스와의 100+ 프로젝트 경험을 바탕으로 현장에서 검증된 안정적인 자동화 시스템을 제공합니다.
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                        {METRICS.map((m) => (
                        <MetricBlock key={m.label} m={m} />
                        ))}
                    </div>
                    </aside>

                    {/* 우측 솔루션 영역 리스트 */}
                    <div style={{ display: "grid", gap: 20 }}>
                    {SOLUTION_AREAS.map((s) => (
                        <article
                        key={s.title}
                        style={{
                            background: "#2a2a2a",
                            border: "1px solid #333",
                            borderRadius: 8,
                            padding: 28,
                        }}
                        >
                        <div style={{ fontSize: 18, color: "#3498db", marginBottom: 10, fontWeight: 400, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            {s.title}
                        </div>
                        <p style={{ color: "#ccc", lineHeight: 1.7, marginBottom: 16 }}>{s.description}</p>
                        {!!s.tags?.length && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                            {s.tags.map((t) => (
                                <span
                                key={t}
                                style={{
                                    background: "rgba(52,152,219,.1)",
                                    color: "#3498db",
                                    padding: "8px 12px",
                                    borderRadius: 4,
                                    fontSize: 12,
                                    letterSpacing: 0.5,
                                    textTransform: "uppercase",
                                }}
                                >
                                {t}
                                </span>
                            ))}
                            </div>
                        )}
                        </article>
                    ))}
                    </div>
                </div>
                </div>
            </section>

            {/* Field-First Approach */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <div className={style["text--content"]}>Field-First Approach</div>
                <p className={style["text--content--copy"]} style={{ width: "min(760px,100%)" }}>
                    현장의 실질적 요구사항을 바탕으로 한 실용적 엔지니어링 방법론
                </p>

                <div
                    style={{
                    background: "linear-gradient(135deg,#2c3e50,#34495e)",
                    border: "1px solid rgba(255,255,255,.12)",
                    borderRadius: 8,
                    padding: "40px 24px",
                    marginTop: 24,
                    }}
                >
                    <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, auto)",
                        gridTemplateRows: "repeat(2, auto)",
                        gap: 20,
                    }}
                    >
                    {APPROACH.map((a) => (
                        <article
                        key={a.title}
                        style={{
                            background: "rgba(255,255,255,.05)",
                            border: "1px solid rgba(255,255,255,.12)",
                            borderRadius: 8,
                            padding: "28px 22px",
                        }}
                        >
                        <div style={{ fontSize: 16, fontWeight: 400, color: "#fff", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            {a.title}
                        </div>
                        <p style={{ color: "#ccc", fontSize: 14, lineHeight: 1.7 }}>{a.description}</p>
                        </article>
                    ))}
                    </div>
                </div>
                </div>
            </section>

            {/* Concep AI Integration */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <div className={style["text--content"]}>Concep AI Integration</div>
                <p className={style["text--content--copy"]} style={{ width: "min(760px,100%)" }}>
                    현장 검증된 AI 기술로 설계 프로세스의 효율성을 극대화
                </p>

                <div style={{ background: "#2a2a2a", borderRadius: 8, border: "1px solid #333", padding: 32, marginTop: 24 }}>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                    <div style={{ fontSize: 22, color: "#3498db", marginBottom: 6, fontWeight: 400 }}>Smart Design Automation</div>
                    <p style={{ color: "#ccc" }}>
                        기존 엔지니어링 프로세스와 AI 기술을 유기적으로 결합하여 설계 효율성과 정확성을 동시에 향상시킵니다.
                    </p>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, auto)", gridTemplateRows: "repeat(2, auto)", gap: 20, marginTop: 20 }}>
                    {CAPABILITIES.map((c) => (
                        <article
                        key={c.title}
                        style={{
                            textAlign: "center",
                            padding: "22px 16px",
                            background: "rgba(52,152,219,.1)",
                            borderLeft: "4px solid #3498db",
                            borderRadius: 4,
                        }}
                        >
                        <div style={{ fontSize: 14, color: "#3498db", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            {c.title}
                        </div>
                        <div style={{ color: "#ccc", fontSize: 14, lineHeight: 1.6 }}>{c.description}</div>
                        </article>
                    ))}
                    </div>
                </div>
                </div>
            </section>

            {/* Development Process */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <div className={style["text--content"]}>Development Process</div>
                <p className={style["text--content--copy"]} style={{ width: "min(760px,100%)" }}>
                    체계적이고 검증된 5단계 프로세스로 안정적인 시스템 구축 보장
                </p>

                {/* 흐름 카드 */}
                <div style={{ background: "#2a2a2a", border: "1px solid #333", borderRadius: 8, padding: 32, marginTop: 24 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", justifyItems: "center", gap: 24 }}>
                    {STEPS.map((s, i) => (
                        <div key={s.num} style={{ textAlign: "center", position: "relative" }}>
                        <div
                            style={{
                            width: 60,
                            height: 60,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg,#3498db,#2ecc71)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 700,
                            margin: "0 auto 12px",
                            }}
                        >
                            {s.num}
                        </div>
                        <div style={{ color: "#3498db", fontWeight: 400, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                            {s.title}
                        </div>
                        <div style={{ color: "#ccc", fontSize: 14, lineHeight: 1.6 }}>{s.description}</div>
                        {/* 간단한 연결 라인 (모바일에서는 그리드 흐름상 생략) */}
                        {i < STEPS.length - 1 && (
                            <div
                            aria-hidden
                            style={{
                                display: "none",
                            }}
                            />
                        )}
                        </div>
                    ))}
                    </div>
                </div>
                </div>
            </section>

            {/* Quality Assurance */}
            <section className={style["container--portfolio"]}>
                <div className={style["unit--content"]}>
                <div className={style["text--content"]}>Quality Assurance</div>
                <p className={style["text--content--copy"]} style={{ width: "min(760px,100%)" }}>
                    제조업 환경에 최적화된 엄격한 품질 관리 체계
                </p>

                <div
                    style={{
                    background: "linear-gradient(145deg,#1e1e1e,#2a2a2a)",
                    borderRadius: 8,
                    border: "1px solid #333",
                    padding: "36px 28px",
                    marginTop: 24,
                    }}
                >
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, auto)", gridTemplateRows: "repeat(2, auto)", gap: 20 }}>
                    {QUALITY.map((q) => (
                        <article
                        key={q.title}
                        style={{
                            padding: 24,
                            background: "rgba(231,76,60,.1)",
                            borderLeft: "4px solid #e74c3c",
                            borderRadius: 4,
                        }}
                        >
                        <div style={{ color: "#e74c3c", fontWeight: 600, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            {q.title}
                        </div>
                        <div style={{ color: "#ccc", fontSize: 14, lineHeight: 1.6 }}>{q.description}</div>
                        </article>
                    ))}
                    </div>
                </div>
                </div>
            </section>
        </div>
      <Footer />
    </>
  );
}
